import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import GridShape from './common/GridShape';

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  onReset?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, errorInfo, onReset }) => {
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  const handleTryAgain = () => {
    if (onReset) {
      onReset();
    }
    window.location.reload();
  };

  const handleGoHome = () => {
    if (onReset) {
      onReset();
    }
    navigate('/');
  };

  return (
    <div className="relative flex flex-col items-center justify-center min-h-screen p-6 overflow-hidden z-1">
      <GridShape />

      <div className="mx-auto w-full max-w-[480px] text-center">
        {/* Error Icon */}
        <div className="mb-8 flex justify-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/20">
            <svg
              className="w-10 h-10 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Error Title */}
        <h1 className="mb-4 font-bold text-gray-800 text-title-md dark:text-white/90 xl:text-title-xl">
          Something went wrong
        </h1>

        {/* Error Message */}
        {error && (
          <p className="mb-8 text-base text-gray-700 dark:text-gray-400 sm:text-lg">
            {error.message || 'An unexpected error occurred'}
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={handleTryAgain}
            className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3.5 text-sm font-medium text-white shadow-theme-xs hover:bg-brand-600 transition"
          >
            Try Again
          </button>
          <button
            onClick={handleGoHome}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-3.5 text-sm font-medium text-gray-700 shadow-theme-xs hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200 transition"
          >
            Go Home
          </button>
        </div>

        {/* Error Details Toggle */}
        {(error || errorInfo) && (
          <div className="mt-8">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 underline transition"
            >
              {showDetails ? 'Hide' : 'Show'} error details
            </button>

            {showDetails && (
              <div className="mt-4 p-4 text-left bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800">
                {error && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-white/90 mb-2">
                      Error Message:
                    </h3>
                    <pre className="text-xs text-red-600 dark:text-red-400 whitespace-pre-wrap break-words">
                      {error.toString()}
                    </pre>
                  </div>
                )}

                {error?.stack && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-800 dark:text-white/90 mb-2">
                      Stack Trace:
                    </h3>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words overflow-x-auto max-h-48 overflow-y-auto">
                      {error.stack}
                    </pre>
                  </div>
                )}

                {errorInfo?.componentStack && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-800 dark:text-white/90 mb-2">
                      Component Stack:
                    </h3>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words overflow-x-auto max-h-48 overflow-y-auto">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <p className="absolute text-sm text-center text-gray-500 -translate-x-1/2 bottom-6 left-1/2 dark:text-gray-400">
        &copy; {new Date().getFullYear()} - RegulAgent
      </p>
    </div>
  );
};

export default ErrorFallback;
